import React, { useCallback, useEffect, useRef, useState } from 'react';

const EmptyPlayer = ({
  id,
  active,
  className,
  wrapperClassName,
  wrapperStyle,
  innerRef,
  poster,
  width,
  height,
  playing,
  playbackRate,
  scenes,
  volume,
  muted,
  onReady,
  onStart,
  onPlay,
  onPause,
  onBuffer,
  onBufferEnd,
  onSeek,
  onEnded,
  onError,
  onProgress,
  onDuration,
  progressInterval = 50,
  style,
  currentZoomStyles = {},
}) => {
  const [notPlayedYet, setNotPlayedYet] = useState(true);
  const [duration, setDuration] = useState(0);
  const [intervalState, setIntervalState] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(0);

  const lastPlayPos = useRef(0);
  const playerRef = useRef(null);
  const interval = useRef(null);
  const bufferingTimeout = useRef(null);

  const emptySourceDetectedRef = useRef(null);
  const startWhenReadyRef = useRef(null);

  innerRef.current = playerRef.current;

  const onSeekTo = useCallback(
    (value, type = 'seconds') => {
      if (playerRef.current) {
        if (type === 'seconds') {
          playerRef.current.currentTime = value;
        } else {
          const seconds = value * duration;
          playerRef.current.currentTime = seconds;
        }
      }
    },
    [duration]
  );

  const handleSeeking = useCallback(
    e => {
      onSeek(e.target.currentTime);
      if (!playing) {
        onProgress({ played: e.target.currentTime / duration, playedSeconds: e.target.currentTime });
      }
    },
    [onSeek, playing, onProgress]
  );

  const handleDuration = useCallback(
    e => {
      if (e.target?.duration > 0 && e.target?.duration !== duration) {
        setDuration(e.target?.duration);
        onDuration(e.target?.duration);
      }
    },
    [onDuration]
  );

  const handleEnded = useCallback(
    e => {
      onEnded(e);
      setTimeout(() => {
        if (playerRef?.current?.currentTime >= 0) {
          playerRef.current.currentTime = 0;
        }
      }, 500);
    },
    [onEnded]
  );

  const handleEmptied = useCallback(() => {
    // In case hls instance was detached from the player, but the playing flag is still true - reset it
    // The flag should be reset cause detaching source stops playing
    if (playing) {
      playerRef.current.pause();
      emptySourceDetectedRef.current = true;
    }
  }, [playing, onPause]);

  if (playerRef.current) {
    playerRef.current.onwaiting = () => {
      const buffered = playerRef.current.buffered;
      const currentTime = playerRef.current.currentTime;
      const duration = playerRef.current.duration;

      // Get the last buffered range
      let bufferedEnd = 0;
      if (buffered.length > 0) {
        bufferedEnd = buffered.end(buffered.length - 1);
      }

      // Check if the current time is within the buffered range and if we have buffered up to the end
      if (bufferedEnd >= currentTime && bufferedEnd >= duration - 0.1 && bufferedEnd - currentTime < 0.1) {
        // 0.1 to allow for a small tolerance
        // If it's 100ms or less before the end of the video, send progress video ended
        onProgress({ played: 1, playedSeconds: bufferedEnd });
        console.log('Video is buffered until the end', { bufferedEnd, currentTime, diff: bufferedEnd - currentTime });
        return;
      }
      // Buffering is detected
      bufferingTimeout.current = setTimeout(() => {
        onBuffer();
        bufferingTimeout.current = null;
      }, 350);
      console.log('Buffering detected', { bufferedEnd, currentTime, duration });
    };

    playerRef.current.onplaying = () => {
      if (bufferingTimeout.current) {
        clearTimeout(bufferingTimeout.current);
        bufferingTimeout.current = null;
      } else {
        onBufferEnd();
      }
    };

    playerRef.current.onloadeddata = () => {
      onReady();
      if (startWhenReadyRef.current) {
        innerRef?.current
          ?.play()
          ?.catch(e => console.log('Error play interrupted', e))
          .finally(() => onBufferEnd());
        startWhenReadyRef.current = false;
      }
      if (emptySourceDetectedRef.current && !startWhenReadyRef.current) {
        playerRef.current.play();
        emptySourceDetectedRef.current = null;
      }
    };

    playerRef.current.oncanplay = () => {
      if (bufferingTimeout.current) {
        clearTimeout(bufferingTimeout.current);
        bufferingTimeout.current = null;
      } else {
        onBufferEnd();
      }
      emptySourceDetectedRef.current && playerRef.current.play();
      emptySourceDetectedRef.current = null;
    };
  }

  useEffect(() => {
    innerRef.current = playerRef.current;
    innerRef.current.seekTo = onSeekTo;
  }, [duration, onSeekTo]);

  useEffect(() => {
    if (playing) {
      if (innerRef?.current?.readyState === HTMLMediaElement.HAVE_NOTHING) {
        onBuffer();
        startWhenReadyRef.current = true;
      } else if (innerRef?.current?.readyState >= HTMLMediaElement.HAVE_METADATA && innerRef?.current?.paused) {
        innerRef?.current
          ?.play()
          ?.catch(e => console.log('Error play interrupted', e))
          .finally(() => onBufferEnd());
      }
      if (notPlayedYet) {
        setNotPlayedYet(false);
        onStart();
      }
      if (!interval?.current) {
        interval.current = setInterval(() => {
          setIntervalState(true);
        }, progressInterval);
      }
    } else {
      !innerRef?.current?.paused && innerRef?.current?.pause()?.catch(e => console.log('Error pause interrupted', e));
      onBufferEnd();
      clearInterval(interval.current);
      startWhenReadyRef.current = false;
      interval.current = null;
    }
  }, [playing]);

  useEffect(() => {
    if (!active) {
      lastPlayPos.current = null;
      if (innerRef?.current) {
        !innerRef.current.paused && innerRef?.current?.pause();
      }
    }
  }, [active]);

  useEffect(() => {
    return () => {
      clearInterval(interval.current);
      interval.current = null;
    };
  }, []);

  useEffect(() => {
    if (intervalState) {
      playerRef.current.currentTime !== lastUpdated &&
        onProgress({ played: playerRef?.current?.currentTime / duration, playedSeconds: playerRef?.current?.currentTime });
      setLastUpdated(playerRef.current.currentTime);
      setIntervalState(false);
    }
  }, [intervalState]);

  useEffect(() => {
    if (playerRef?.current?.paused && duration > 0) {
      onProgress({ played: playerRef?.current?.currentTime / duration, playedSeconds: playerRef?.current?.currentTime });
    }
  }, [playerRef?.current?.currentTime]);

  useEffect(() => {
    if (playerRef.current) {
      if (playerRef.current.playbackRate !== playbackRate) {
        playerRef.current.playbackRate = 1;
        setTimeout(() => {
          if (playerRef.current) {
            playerRef.current.playbackRate = playbackRate;
          }
        }, 100);
      }
    }
  }, [playbackRate]);

  useEffect(() => {
    playerRef.current.volume = volume;
  }, [volume]);

  return (
    <div id={id} className={className}>
      <div className={wrapperClassName} style={wrapperStyle}>
        <video
          type="application/x-mpegURL"
          ref={playerRef}
          width={width}
          height={height}
          muted={muted}
          style={{
            ...style, visibility: poster ? 'visible' : 'hidden', ...(currentZoomStyles ?? {}),
          }}
          onEnded={handleEnded}
          onError={onError}
          onSeeked={handleSeeking}
          onDurationChange={handleDuration}
          onPlay={onPlay}
          onPause={onPause}
          crossOrigin={'false'}
          preload={'none'}
          playsInline={true}
          controlsList={'nodownload'}
          onEmptied={handleEmptied}
        />
      </div>
    </div>
  );
};

export default EmptyPlayer;
